/** Epic 91 v1 hazard-class keys (must match cloud hazard_class_registry). */

export type SafetyReportTypeMode = 'pattern' | 'hazard_class';

export type HazardClassKey =
  | 'mobile_equipment_separation'
  | 'restricted_zone_intrusion'
  | 'ppe_compliance'
  | 'worker_down_response';

export type HazardClassOption = {
  key: HazardClassKey;
  displayName: string;
  executiveRiskLabel: string;
  claimBoundary: string;
};

export const DEFAULT_SAFETY_REPORT_TYPE_MODE: SafetyReportTypeMode = 'pattern';

export const DEFAULT_HAZARD_CLASS_KEY: HazardClassKey = 'mobile_equipment_separation';

/** Display names aligned with cloud get_hazard_class_config (Epic 91a). */
export const V1_HAZARD_CLASS_OPTIONS: HazardClassOption[] = [
  {
    key: 'mobile_equipment_separation',
    displayName: 'Mobile Equipment Separation',
    executiveRiskLabel: 'Pedestrian / mobile equipment interaction',
    claimBoundary:
      'Detects unsafe proximity, zone breach, and co-location precursors. Does not claim collision prediction, strike prevention, or risk elimination.',
  },
  {
    key: 'restricted_zone_intrusion',
    displayName: 'Restricted Zone Entry',
    executiveRiskLabel: 'Unauthorized or unsafe entry into hazardous operating areas',
    claimBoundary:
      'Detects zone entry, dwell, and loitering in restricted areas. Does not prove injury risk was eliminated or that barriers are adequate.',
  },
  {
    key: 'ppe_compliance',
    displayName: 'PPE Compliance Visibility',
    executiveRiskLabel: 'PPE visibility and compliance gaps',
    claimBoundary:
      'Detects PPE visibility gaps on camera. Does not prove injury prevention or regulatory certification.',
  },
  {
    key: 'worker_down_response',
    displayName: 'Worker Down / Delayed Response',
    executiveRiskLabel: 'Delayed response to worker-down conditions',
    claimBoundary:
      'Detects possible worker-down conditions. Does not diagnose medical condition, fall cause, or guarantee emergency response time.',
  },
];

export function isHazardClassKey(value: string): value is HazardClassKey {
  return V1_HAZARD_CLASS_OPTIONS.some((option) => option.key === value);
}

export function resolveHazardClassOption(key: string): HazardClassOption | null {
  const normalized = String(key || '').trim().toLowerCase();
  return V1_HAZARD_CLASS_OPTIONS.find((option) => option.key === normalized) || null;
}

export function parseSafetyReportTypeMode(raw: string | null | undefined): SafetyReportTypeMode {
  return String(raw || '').trim().toLowerCase() === 'hazard_class' ? 'hazard_class' : 'pattern';
}

export const HAZARD_CLASS_REPORT_CONTROL_SUBTITLE =
  'Hazard-class safety intelligence report summarizing recurring risks for one precursor family, evidence readiness, review status, ownership, corrective action, and claim boundaries.';

export function resolveCustomerReportTitle(
  mode: SafetyReportTypeMode,
  hazardClassKey?: HazardClassKey | string,
): string {
  if (mode !== 'hazard_class') {
    return 'Safety Report';
  }
  const option = resolveHazardClassOption(String(hazardClassKey || DEFAULT_HAZARD_CLASS_KEY));
  return option ? `${option.displayName} Report` : 'Risk category report';
}

export function resolveCustomerReportSubtitle(mode: SafetyReportTypeMode): string {
  return mode === 'hazard_class'
    ? HAZARD_CLASS_REPORT_CONTROL_SUBTITLE
    : 'Evidence-ready report showing recurring risk patterns, pattern observations, ownership, corrective action status, and impact measurement readiness.';
}

export function resolveReportBuildingBannerText(
  mode: SafetyReportTypeMode,
  hazardClassKey?: HazardClassKey | string,
): string {
  if (mode !== 'hazard_class') {
    return 'Building Safety Report ZIP package...';
  }
  const option = resolveHazardClassOption(String(hazardClassKey || DEFAULT_HAZARD_CLASS_KEY));
  const label = option?.displayName || 'Hazard-Class';
  return `Building ${label} ZIP package...`;
}

export function resolveReportBuildingNotice(
  mode: SafetyReportTypeMode,
  hazardClassKey?: HazardClassKey | string,
): string {
  if (mode !== 'hazard_class') {
    return 'Building Safety Report ZIP package… this may take several minutes for wide time windows.';
  }
  const option = resolveHazardClassOption(String(hazardClassKey || DEFAULT_HAZARD_CLASS_KEY));
  const label = option?.displayName || 'Hazard-Class';
  return `Building ${label} ZIP package… this may take several minutes for wide time windows.`;
}

export function resolveReportDownloadButtonLabel(
  mode: SafetyReportTypeMode,
  hazardClassKey?: HazardClassKey | string,
  _options?: { signOffReady?: boolean },
): string {
  if (mode !== 'hazard_class') {
    return 'Download Package';
  }
  const option = resolveHazardClassOption(String(hazardClassKey || DEFAULT_HAZARD_CLASS_KEY));
  const label = option?.displayName || 'Hazard-Class';
  return `Download ${label} Package`;
}

/** Short product name for helpers, technical copy, and download notices. */
export function resolveReportProductName(
  mode: SafetyReportTypeMode,
  hazardClassKey?: HazardClassKey | string,
): string {
  if (mode !== 'hazard_class') {
    return 'Safety Scan';
  }
  const option = resolveHazardClassOption(String(hazardClassKey || DEFAULT_HAZARD_CLASS_KEY));
  return option ? `${option.displayName} Hazard-Class Report` : 'Hazard-Class Report';
}
